import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addToast } from '@heroui/react';

import { MAX_SPEC_CONTENT_LENGTH } from '~/constants/prd';
import { usePRDBreakdown } from '~/hooks/usePRDBreakdown';
import { usePRDGeneration } from '~/hooks/usePRDGeneration';
import { usePRDSessionStatus } from '~/hooks/usePRDSessionStatus';
import { useSSEStore } from '~/stores/sseStore';
import { useUIStore } from '~/stores/uiStore';
import { logger } from '~/utils/logger';

import type { ProjectProps } from '~/types';

import { PRDBreakdownStatusPanel } from './PRDBreakdownStatusPanel';
import { PRDGenerationStatusPanel } from './PRDGenerationStatusPanel';

const DETAILS_MAX_LENGTH = MAX_SPEC_CONTENT_LENGTH;

interface ProjectCardPRDPanelProps {
  project: ProjectProps;
  onGenerateTasksFromPRD: (projectId: string, prdContent: string) => Promise<void>;
}

interface StartSessionDetailsResult {
  error?: string;
  value?: string;
}

function buildStartSessionProjectDetails(
  description?: string | null,
  details?: string | null,
): StartSessionDetailsResult {
  const normalizedDescription = description?.trim() || '';
  const normalizedDetails = details?.trim() || '';

  if (normalizedDetails.length > DETAILS_MAX_LENGTH) {
    return {
      error: `Project details exceed ${DETAILS_MAX_LENGTH.toLocaleString()} characters. Please shorten details and try again.`,
    };
  }

  if (normalizedDescription && normalizedDetails) {
    if (normalizedDescription === normalizedDetails) {
      return { value: normalizedDetails };
    }

    const combined = `Project summary:\n${normalizedDescription}\n\nUser details:\n${normalizedDetails}`;
    return {
      value: combined.length <= DETAILS_MAX_LENGTH ? combined : normalizedDetails,
    };
  }

  const single = normalizedDetails || normalizedDescription;
  return { value: single || undefined };
}

/**
 * PRD generation and breakdown status panels for ProjectCard.
 * Owns all PRD generation state, SSE job tracking, and auto-start logic.
 */
export function ProjectCardPRDPanel(props: ProjectCardPRDPanelProps): JSX.Element {
  const { onGenerateTasksFromPRD, project } = props;

  // Breakdown status tracking with automatic polling
  const {
    cancel: cancelBreakdown,
    isCanceling,
    status: breakdownStatus,
  } = usePRDBreakdown(project.id);

  // PRD generation session status tracking (batch polling)
  const {
    cancel: cancelSession,
    isCanceling: isCancelingSession,
    status: sessionStatus,
  } = usePRDSessionStatus(project.id);

  // SSE store for real-time updates during PRD generation
  const addActiveJob = useSSEStore(state => state.addActiveJob);
  const removeActiveJob = useSSEStore(state => state.removeActiveJob);
  const activeJobIdRef = useRef<string | null>(null);

  // PRD generation hooks
  const { startSession } = usePRDGeneration(project.id);

  // Track if mutation is pending (button is loading)
  const isPRDGenerating = startSession.isPending;

  // Track when generation started (set directly in handleGeneratePRD, cleared when mutation completes)
  const generationStartTimeRef = useRef<string | null>(null);

  // PRD panel dismiss state (reset when new generation starts)
  const [prdPanelDismissed, setPrdPanelDismissed] = useState(false);

  // Derive PRD panel visibility from authoritative state
  const showPRDPanel =
    !prdPanelDismissed &&
    (isPRDGenerating || sessionStatus?.status === 'active' || sessionStatus?.status === 'paused');

  // Breakdown panel state
  const [showBreakdownPanel, setShowBreakdownPanel] = useState(true);

  // UI store for opening assistant panel and switching tabs
  const { setActiveProjectAssistantTab, setProjectAssistantExpanded } = useUIStore();

  // Navigation hook for PRD Review
  const navigate = useNavigate();

  // Register/remove SSE active job for real-time updates during PRD generation
  useEffect(() => {
    const isActive = sessionStatus?.status === 'active';
    const sessionId = sessionStatus?.session_id;

    if (isActive && sessionId) {
      if (!activeJobIdRef.current) {
        const jobId = `prd-card-${project.id}`;
        activeJobIdRef.current = jobId;
        addActiveJob(jobId, project.id, 120);
        logger.debug(
          { component: 'ProjectCard' },
          'Registered SSE job for active PRD session (page refresh)',
          { projectId: project.id, jobId },
        );
      }
    } else {
      if (activeJobIdRef.current) {
        const jobId = activeJobIdRef.current;
        removeActiveJob(jobId);
        activeJobIdRef.current = null;
        logger.debug({ component: 'ProjectCard' }, 'Removed SSE job for PRD generation', {
          projectId: project.id,
          jobId,
          status: sessionStatus?.status,
        });
      }
    }

    return (): void => {
      if (activeJobIdRef.current) {
        removeActiveJob(activeJobIdRef.current);
        activeJobIdRef.current = null;
      }
    };
  }, [sessionStatus?.status, sessionStatus?.session_id, project.id, addActiveJob, removeActiveJob]);

  // Hide breakdown panel when there's no active task breakdown
  useEffect(() => {
    if (
      !breakdownStatus?.has_active_breakdown &&
      breakdownStatus?.breakdown_status === 'idle' &&
      showBreakdownPanel
    ) {
      setShowBreakdownPanel(false);
    }
  }, [breakdownStatus, showBreakdownPanel]);

  // Auto-show breakdown panel when job completes or fails
  useEffect(() => {
    if (
      breakdownStatus?.breakdown_status === 'COMPLETED' ||
      breakdownStatus?.breakdown_status === 'FAILED' ||
      breakdownStatus?.breakdown_status === 'CANCELLED'
    ) {
      setShowBreakdownPanel(true);
    }
  }, [breakdownStatus?.breakdown_status]);

  // Handle PRD generation (autonomous - no Q&A needed)
  const handleGeneratePRD = useCallback(async () => {
    if (isPRDGenerating || sessionStatus?.status === 'active') {
      addToast({ color: 'warning', description: 'PRD generation already in progress.' });
      return;
    }

    const projectDetailsResult = buildStartSessionProjectDetails(
      project.description,
      project.details,
    );
    if (projectDetailsResult.error) {
      addToast({ color: 'danger', description: projectDetailsResult.error });
      return;
    }

    // Record generation start time for the status panel timestamp
    generationStartTimeRef.current = new Date().toISOString();
    // Reset dismiss so panel shows for new generation
    setPrdPanelDismissed(false);

    // Register SSE job IMMEDIATELY before API call to capture all events
    const jobId = `prd-card-${project.id}`;
    if (!activeJobIdRef.current) {
      activeJobIdRef.current = jobId;
      addActiveJob(jobId, project.id, 120);
      logger.info({ component: 'ProjectCard' }, 'Registered SSE job before PRD generation start', {
        projectId: project.id,
        jobId,
      });
    }

    try {
      await startSession.mutateAsync({
        project_id: project.id,
        project_title: project.title,
        project_details: projectDetailsResult.value,
      });

      // Clear generation start time on success
      generationStartTimeRef.current = null;

      await new Promise(resolve => setTimeout(resolve, 500));

      void navigate(`/projects/${project.id}/review`);

      addToast({ color: 'success', description: 'PRD generated successfully' });
    } catch (error) {
      // Clear generation start time on failure
      generationStartTimeRef.current = null;

      if (activeJobIdRef.current) {
        removeActiveJob(activeJobIdRef.current);
        activeJobIdRef.current = null;
        logger.info({ component: 'ProjectCard' }, 'Removed SSE job after PRD generation failure', {
          projectId: project.id,
        });
      }

      logger.error(
        { component: 'ProjectCard', projectId: project.id },
        'Failed to start PRD generation',
        error instanceof Error ? error : new Error(String(error)),
      );
      addToast({ color: 'danger', description: 'Failed to start PRD generation' });
    }
  }, [
    project,
    sessionStatus,
    startSession,
    isPRDGenerating,
    navigate,
    addActiveJob,
    removeActiveJob,
  ]);

  // Resume is same as start for autonomous generation
  const handleResumePRD = useCallback(async () => {
    await handleGeneratePRD();
  }, [handleGeneratePRD]);

  // Actually start the breakdown after confirmation
  const handleConfirmGenerate = useCallback(async () => {
    const prdContent = project.prdContent;
    if (!prdContent) {
      addToast({
        color: 'danger',
        description: 'PRD content is missing. Please generate a PRD first.',
      });
      return;
    }

    try {
      await onGenerateTasksFromPRD(project.id, prdContent);
      setProjectAssistantExpanded(true);
      setActiveProjectAssistantTab('progress');
      setShowBreakdownPanel(true);
    } catch (error) {
      logger.error(
        { component: 'ProjectCard', projectId: project.id },
        'Failed to generate tasks',
        error instanceof Error ? error : new Error(String(error)),
      );
      addToast({
        color: 'danger',
        description: 'Failed to start task generation. Please try again.',
      });
    }
  }, [
    project.id,
    project.prdContent,
    onGenerateTasksFromPRD,
    setProjectAssistantExpanded,
    setActiveProjectAssistantTab,
  ]);

  // Auto-start PRD generation when pendingPRDAutoStart matches this project.
  // Uses Zustand subscribe so the mutation fires from an explicit store-change trigger.
  const autoStartFiredRef = useRef(false);
  const handleGeneratePRDRef = useRef(handleGeneratePRD);
  handleGeneratePRDRef.current = handleGeneratePRD;

  useEffect(() => {
    const checkAndAutoStart = (): void => {
      if (autoStartFiredRef.current) return;
      const { clearPendingPRDAutoStart, pendingPRDAutoStart } = useUIStore.getState();

      if (pendingPRDAutoStart === project.id && !project.id.startsWith('temp-')) {
        autoStartFiredRef.current = true;
        logger.info(
          { component: 'ProjectCard', projectId: project.id },
          'Auto-starting PRD generation for newly created project',
        );
        clearPendingPRDAutoStart();
        void handleGeneratePRDRef.current();
      }
    };

    checkAndAutoStart();

    const unsubscribe = useUIStore.subscribe(checkAndAutoStart);
    return unsubscribe;
  }, [project.id]);

  // Keep handleConfirmGenerate available but unused for now (used by confirmation dialog in parent)
  void handleConfirmGenerate;

  return (
    <>
      {/* PRD Generation Status Panel - show during mutation OR when status is active */}
      {showPRDPanel && (
        <PRDGenerationStatusPanel
          projectId={project.id}
          status={
            isPRDGenerating
              ? {
                  project_id: project.id,
                  session_id: 'pending',
                  status: 'active',
                  session_state: 'generating',
                  generation_phase: 'classifying',
                  completeness_score: 0,
                  question_count: 0,
                  answered_question_count: 0,
                  created_at: generationStartTimeRef.current || new Date().toISOString(),
                  updated_at: generationStartTimeRef.current || new Date().toISOString(),
                  is_owner: true,
                }
              : sessionStatus
          }
          onCancel={cancelSession}
          isCanceling={isCancelingSession}
          onDismiss={() => setPrdPanelDismissed(true)}
          onResume={() => {
            void handleResumePRD();
          }}
        />
      )}

      {/* PRD Breakdown Status Panel */}
      {breakdownStatus && showBreakdownPanel && breakdownStatus.breakdown_status !== 'idle' && (
        <PRDBreakdownStatusPanel
          projectId={project.id}
          status={breakdownStatus}
          onCancel={async () => {
            await cancelBreakdown();
          }}
          isCanceling={isCanceling}
          onDismiss={() => setShowBreakdownPanel(false)}
        />
      )}
    </>
  );
}
